import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Menu, Button, Drawer, Typography, Avatar, Tooltip } from 'antd';
import {
  MenuUnfoldOutlined,
  DashboardOutlined,
  BarChartOutlined,
  FileTextOutlined,
  LogoutOutlined,
  UserOutlined,
  LeftOutlined,
  RightOutlined,
  FolderOpenOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';

const { Sider } = Layout;

interface SidebarProps {
  collapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed = false, onCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout: authLogout } = useAuth();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [userRole, setUserRole] = useState<string | null>(user?.rol || null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);

    // Actualizar el rol cuando cambia en localStorage
    const updateRole = () => {
      setUserRole(localStorage.getItem('rol'));
    };

    // Inicialmente actualizar el rol
    updateRole();

    // Configurar un event listener para storage
    window.addEventListener('storage', updateRole);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('storage', updateRole);
    };
  }, []);

  // Generar los items del menú dinámicamente según el rol
  const getMenuItems = () => {
    // Elementos base que todos los usuarios pueden ver
    const baseItems = [
      { key: '/', icon: <FolderOpenOutlined />, label: <Link to="/">Registros</Link> },
      { key: '/graficos', icon: <BarChartOutlined />, label: <Link to="/graficos">Gráficos</Link> },
    ];

    // Solo los administradores pueden ver Reportes y Panel de Control
    if (userRole === 'admin') {
      baseItems.push(
        {
          key: '/reportes',
          icon: <FileTextOutlined />,
          label: <Link to="/reportes">Reportes</Link>,
        },
        {
          key: '/config-reportes',
          icon: <SettingOutlined />,
          label: <Link to="/config-reportes">Config. Reportes</Link>,
        },
        {
          key: '/panel-control',
          icon: <DashboardOutlined />,
          label: <Link to="/panel-control">Panel de Control</Link>,
        }
      );
    }

    return baseItems;
  };

  const handleLogout = () => {
    authLogout();
    setUserRole(null);
    navigate('/login');
  };

  const userFullName = user
    ? `${user.nombre || user.username} ${user.apellido || ''}`.trim()
    : localStorage.getItem('username') || '';

  const sidebarContent = (
    <>
      <div
        style={{
          height: 80,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1e293b',
          padding: 10,
        }}
      >
        <img
          src="/sq-logo.png"
          alt="Logo"
          style={{ width: 64, height: 64, objectFit: 'contain' }}
        />
        <Typography.Text
          style={{
            color: 'rgba(255,255,255,0.7)',
            fontSize: collapsed ? 10 : 12,
            display: 'block',
            marginTop: 2,
          }}
        >
          SEPRO APP
        </Typography.Text>
        <Typography.Text
          style={{ color: 'rgba(255,255,255,0.5)', fontSize: collapsed ? 9 : 11, display: 'block' }}
        >
          ver. 3.0
        </Typography.Text>
      </div>
      <div style={{ textAlign: 'center', color: 'white', margin: '16px 0' }}>
        <Avatar
          size={48}
          icon={<UserOutlined />}
          style={{ backgroundColor: '#1890ff', marginBottom: 8 }}
        />
        {!collapsed && (
          <Typography.Text style={{ display: 'block', color: 'white', fontWeight: 'bold' }}>
            {userFullName}
          </Typography.Text>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={getMenuItems()}
        style={{ flex: 1, background: '#1e293b', marginTop: 8 }}
        onClick={() => isMobile && setDrawerVisible(false)}
      />
      <div
        style={{
          textAlign: 'center',
          color: 'rgba(255,255,255,0.7)',
          borderTop: '1px solid rgba(255,255,255,0.12)',
          padding: 16,
          position: 'relative',
          minHeight: 80,
        }}
      >
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 48 }}>
          {/* Botón de cerrar sesión */}
          {collapsed ? (
            <Tooltip title="Cerrar sesión" placement="right">
              <Button
                shape="circle"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                style={{
                  background: '#888',
                  color: 'white',
                  border: 'none',
                  boxShadow: '0 2px 8px rgba(136,136,136,0.15)',
                }}
              />
            </Tooltip>
          ) : (
            <Button
              icon={<LogoutOutlined />}
              onClick={handleLogout}
              style={{
                width: 'auto',
                justifyContent: 'center',
                display: 'inline-flex',
                background: '#888',
                color: 'white',
                border: 'none',
                boxShadow: '0 2px 8px rgba(136,136,136,0.15)',
              }}
              type="default"
            >
              Cerrar sesión
            </Button>
          )}
        </div>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <>
        <Button
          type="primary"
          icon={<MenuUnfoldOutlined />}
          onClick={() => setDrawerVisible(true)}
          style={{ position: 'fixed', top: 16, left: 16, zIndex: 2000 }}
        />
        <Drawer
          placement="left"
          closable={false}
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          width={220}
          bodyStyle={{ padding: 0, background: '#1e293b', color: 'white' }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  return (
    <Sider
      width={220}
      collapsedWidth={80}
      style={{
        background: '#1e293b',
        color: 'white',
        minHeight: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      trigger={null}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{sidebarContent}</div>
      {/* Botón de colapsar/expandir */}
      <div style={{ width: '100%', textAlign: 'center', marginBottom: 10 }}>
        <Tooltip title={collapsed ? 'Expandir' : 'Colapsar'} placement="right">
          <Button
            shape="circle"
            icon={collapsed ? <RightOutlined /> : <LeftOutlined />}
            onClick={() => onCollapse && onCollapse(!collapsed)}
            style={{
              background: 'linear-gradient(135deg, #1890ff 0%, #52c41a 100%)',
              color: 'white',
              boxShadow: '0 2px 8px rgba(24,144,255,0.15)',
              border: 'none',
              fontSize: 22,
              width: 30,
              height: 30,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              float: 'none',
              transition: 'background 0.2s',
              marginBottom: 10,
            }}
          />
        </Tooltip>
      </div>
    </Sider>
  );
};

export default Sidebar;
